'use client';

import { useReporting } from '@/hooks/useReporting';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { priceInfo as getPriceInfo } from '@/utils/price';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { getCampaignTrackingCommonProperties } from '../../hooks/campaignTrackingProperties';
import { useCampaignPageTracking } from '../../hooks/useCampaignPageTracking';
import type { MarketingRuntimeConfig } from '../../types';
import type { CollaborationBannerData } from './types';

export const COLLABORATION_TRACKING_MODULES = {
  seriesProduct: '联名页-系列商品',
  gallery: '联名页-媒体图集',
  productList: '联名页-商品列表',
} as const;

export type CollaborationAssetType = '视频' | '图片';
export type CollaborationMediaClickArea = '播放按钮' | '左侧点击' | '右侧点击';

type TrackingProduct = Record<string, unknown>;

function asTrackingProduct(value: unknown): TrackingProduct {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as TrackingProduct)
    : {};
}

function trackingText(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
}

function productProperties(value: unknown) {
  const product = asTrackingProduct(value);
  return {
    product_sku:
      trackingText(product.shop_sku) || trackingText(product.shopSku) || trackingText(product.sku),
    product_id: trackingText(product.id),
    product_name: trackingText(product.short_name) || trackingText(product.name),
  };
}

function itemProperties(
  value: unknown,
  index: number,
  itemType: 0 | 1,
  price: { price?: unknown; sell_price?: unknown },
) {
  const product = asTrackingProduct(value);
  return {
    item_id: trackingText(product.id),
    item_sku:
      trackingText(product.shop_sku) || trackingText(product.shopSku) || trackingText(product.sku),
    item_name: trackingText(product.short_name) || trackingText(product.name),
    item_price: trackingText(price.price),
    item_current_price: trackingText(price.sell_price),
    item_type: itemType,
    item_index: index,
  };
}

export function useCollaborationEventTracking(runtimeConfig: MarketingRuntimeConfig) {
  const { trackEvent } = useReporting();
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const mediaExposureRef = useRef(new Set<string>());
  const commonProperties = useMemo(
    () => getCampaignTrackingCommonProperties(runtimeConfig),
    [runtimeConfig],
  );

  const report = useCallback(
    (eventName: string, properties: Record<string, unknown>) => {
      try {
        trackEvent({ eventName, properties: { ...commonProperties, ...properties } }, [
          ProviderType.SENSORS,
        ]);
      } catch (error) {
        logger.error('Collaboration campaign tracking error:', error);
      }
    },
    [commonProperties, trackEvent],
  );

  const trackBanner = useCallback(
    (
      eventName: 'CampaignPageValidMkt_Exp' | 'CampaignPageValidMkt_click',
      banner: Pick<CollaborationBannerData, 'materialType' | 'moduleId' | 'title'>,
    ) => {
      report(eventName, {
        module_index: 0,
        module_id: banner.moduleId,
        ...(banner.title ? { module_title: banner.title } : {}),
        asset_type: banner.materialType === 'video' ? '视频' : '图片',
      });
    },
    [report],
  );

  const trackSeriesProduct = useCallback(
    (
      eventName: 'CampaignPagecobrand_component_exp' | 'CampaignPagecobrand_component_click',
      product: unknown,
      mktIndex: number,
      productIndex: number,
      tabName: string,
    ) => {
      report(eventName, {
        mkt_index: mktIndex,
        product_index: productIndex,
        tab_name: tabName,
        ...productProperties(product),
      });
    },
    [report],
  );

  const trackMedia = useCallback(
    (
      eventName: 'CampaignPageMedia_exp' | 'CampaignPageMedia_click',
      mktIndex: number,
      assetType: CollaborationAssetType,
      product?: unknown,
      clickArea?: CollaborationMediaClickArea,
    ) => {
      report(eventName, {
        mkt_index: mktIndex,
        asset_type: assetType,
        ...(product ? productProperties(product) : {}),
        ...(clickArea ? { click_area: clickArea } : {}),
      });
    },
    [report],
  );

  const trackItem = useCallback(
    (
      eventName:
        | 'CampaignPageItemList_Exp'
        | 'CampaignPageItemList_click'
        | 'CampaignPageYouAlsoLikeMkt_Exp'
        | 'CampaignPageYouAlsoLikeMkt_click',
      product: unknown,
      index: number,
      itemType: 0 | 1,
    ) => {
      const trackingProduct = asTrackingProduct(product);
      const price = getPriceInfo(userInfo, trackingProduct, currency) as {
        price?: unknown;
        sell_price?: unknown;
      };
      report(eventName, itemProperties(trackingProduct, index, itemType, price));
    },
    [currency, report, userInfo],
  );

  return {
    trackBannerExposure: (
      banner: Pick<CollaborationBannerData, 'materialType' | 'moduleId' | 'title'>,
    ) => trackBanner('CampaignPageValidMkt_Exp', banner),
    trackBannerClick: (
      banner: Pick<CollaborationBannerData, 'materialType' | 'moduleId' | 'title'>,
    ) => trackBanner('CampaignPageValidMkt_click', banner),
    trackSeriesNavClick: (tabName: string, _index: number) =>
      report('CampaignPageTab_click', { tab_name: tabName }),
    trackSeriesProductClick: (
      product: unknown,
      mktIndex: number,
      productIndex: number,
      tabName: string,
    ) =>
      trackSeriesProduct(
        'CampaignPagecobrand_component_click',
        product,
        mktIndex,
        productIndex,
        tabName,
      ),
    trackSeriesProductExposure: (
      product: unknown,
      mktIndex: number,
      productIndex: number,
      tabName: string,
    ) =>
      trackSeriesProduct(
        'CampaignPagecobrand_component_exp',
        product,
        mktIndex,
        productIndex,
        tabName,
      ),
    trackMediaExposure: (
      mktIndex: number,
      assetType: CollaborationAssetType,
      product?: unknown,
    ) => {
      const key = assetType + ':' + mktIndex + ':' + productProperties(product).product_sku;
      if (mediaExposureRef.current.has(key)) return;
      mediaExposureRef.current.add(key);
      trackMedia('CampaignPageMedia_exp', mktIndex, assetType, product);
    },
    trackMediaClick: (
      mktIndex: number,
      assetType: CollaborationAssetType,
      clickArea: CollaborationMediaClickArea,
      product?: unknown,
    ) => trackMedia('CampaignPageMedia_click', mktIndex, assetType, product, clickArea),
    trackProductListClick: (product: unknown, index: number, _title: string) =>
      trackItem('CampaignPageItemList_click', product, index, 1),
    trackProductListExposure: (product: unknown, index: number, _title: string) =>
      trackItem('CampaignPageItemList_Exp', product, index, 1),
    trackYouAlsoLikeClick: (product: unknown, index: number, _title: string) =>
      trackItem('CampaignPageYouAlsoLikeMkt_click', product, index, 0),
    trackYouAlsoLikeExposure: (product: unknown, index: number, _title: string) =>
      trackItem('CampaignPageYouAlsoLikeMkt_Exp', product, index, 0),
  };
}

export function useCollaborationTracking(runtimeConfig: MarketingRuntimeConfig) {
  const pageTracking = useCampaignPageTracking(runtimeConfig);
  const eventTracking = useCollaborationEventTracking(runtimeConfig);
  const { trackCollectButtonClick, trackPageLeave, trackPageView } = pageTracking;

  useEffect(() => {
    trackPageView();
    return trackPageLeave;
  }, [trackPageLeave, trackPageView]);

  return {
    ...eventTracking,
    trackSeriesProductCollect: (product: unknown, index: number, collectType: string) =>
      trackCollectButtonClick(
        product,
        index,
        COLLABORATION_TRACKING_MODULES.seriesProduct,
        collectType,
      ),
    trackGalleryProductCollect: (product: unknown, index: number, collectType: string) =>
      trackCollectButtonClick(product, index, COLLABORATION_TRACKING_MODULES.gallery, collectType),
    trackProductListCollect: (product: unknown, index: number, collectType: string) =>
      trackCollectButtonClick(
        product,
        index,
        COLLABORATION_TRACKING_MODULES.productList,
        collectType,
      ),
  };
}
